import { Button, Checkbox, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { BudgetSuggestionRow } from 'src/Components/Expenses/BudgetSuggestionRow';
import { IncomeDraftRow } from 'src/Components/Expenses/BudgetWizard/BudgetWizard.types';
import { nextDraftKey } from 'src/Components/Expenses/BudgetWizard/BudgetWizard.utils';
import useConfirmRemoveWizardLine from 'src/Components/Expenses/BudgetWizard/useConfirmRemoveWizardLine';
import { trackBudgetFundCallSuggestionApplied } from 'src/Services/Mixpanel/events';
import {
  BudgetCategory,
  fundCallCategoryOfFrequency,
  FUND_CALL_FREQUENCIES,
  INCOME_CATEGORIES,
  placementOfCategory,
} from 'src/Utils/budgetCategories';
import { draftFundCallAmount, formatEuros } from 'src/Utils/budgetWizardMath';
import { getCategoryLabel } from 'src/Utils/expenseCategories';

import styles from './IncomeStep.module.scss';
import { Props } from './IncomeStep.types';

const FREQUENCY_KEYS: Record<string, string> = {
  '12': 'monthly',
  '4': 'quarterly',
  '1': 'yearly',
  '0.5': 'biennial',
};

const IncomeStep = ({ year, rows, householdCount, financingNeed, onChange }: Props) => {
  const { t } = useTranslation();
  const confirmRemove = useConfirmRemoveWizardLine('incomes');

  const patchRow = (key: string, patch: Partial<IncomeDraftRow>) =>
    onChange(rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const frequencyLabel = (frequencyPerYear: number): string => {
    const key = FREQUENCY_KEYS[String(frequencyPerYear)] ?? 'yearly';
    return t(`budgetV2.income.frequency.${key}`, String(frequencyPerYear));
  };

  const frequencyOptions = FUND_CALL_FREQUENCIES.map(frequency => ({
    value: String(frequency),
    label: frequencyLabel(frequency),
  }));

  const fundCallRow = rows.find(row => row.isFundCall);
  const suggestionFrequency = fundCallRow?.frequencyPerYear ?? 12;
  const suggestedAmount = draftFundCallAmount(financingNeed, householdCount, suggestionFrequency);

  const applySuggestion = () => {
    if (!fundCallRow || suggestedAmount == null) return;
    patchRow(fundCallRow.key, { amount: suggestedAmount, checked: true });
    trackBudgetFundCallSuggestionApplied({
      year,
      frequency_per_year: suggestionFrequency,
      amount_per_household: suggestedAmount,
    });
  };

  // Picking a category adds the income straight away — no button that stays inert.
  const addIncome = (value: string | null) => {
    if (!value) return;
    const category = value as BudgetCategory;
    const placement = placementOfCategory(category);
    onChange([
      ...rows,
      {
        key: nextDraftKey('income'),
        category,
        mainCategory: placement.mainCategory,
        subCategory: placement.subCategory,
        amount: 0,
        frequencyPerYear: 1,
        perHousehold: false,
        checked: true,
        isFundCall: false,
        isManual: true,
      },
    ]);
  };

  const options = INCOME_CATEGORIES.map(category => ({
    value: category,
    label: t(`expense.type.${category}`),
  })).sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  return (
    <div className={styles.container}>
      <div className={styles.list}>
        {rows.map(row => (
          <BudgetSuggestionRow
            key={row.key}
            checked={row.checked}
            onCheckedChange={checked => patchRow(row.key, { checked })}
            title={
              row.isFundCall
                ? t('budgetV2.income.fundCall', 'Appels de fonds')
                : row.category
                  ? t(`expense.type.${row.category}`)
                  : getCategoryLabel(row.mainCategory, row.subCategory)
            }
            meta={
              row.perHousehold
                ? t('budgetV2.income.metaPerHousehold', '{{frequency}} · par foyer (×{{count}})', {
                    frequency: frequencyLabel(row.frequencyPerYear),
                    count: householdCount,
                  })
                : frequencyLabel(row.frequencyPerYear)
            }
            amountLabel={
              row.perHousehold
                ? t('budgetV2.income.amountPerHousehold', 'Montant par foyer')
                : t('budgetV2.amountLabel', 'Montant')
            }
            amount={row.amount}
            onAmountChange={amount => patchRow(row.key, { amount })}
            onRemove={() => {
              const label = row.isFundCall
                ? t('budgetV2.income.fundCall', 'Appels de fonds')
                : row.category
                  ? t(`expense.type.${row.category}`)
                  : getCategoryLabel(row.mainCategory, row.subCategory);
              const totalAmount = row.perHousehold
                ? row.amount * householdCount * row.frequencyPerYear
                : row.amount * row.frequencyPerYear;
              confirmRemove(label, totalAmount, () =>
                onChange(rows.filter(r => r.key !== row.key))
              );
            }}
            removeLabel={t('budgetV2.removeLine', 'Retirer cette ligne')}
          >
            <Select
              className={styles.frequencySelect}
              size="md"
              data={frequencyOptions}
              value={String(row.frequencyPerYear)}
              allowDeselect={false}
              onChange={value => {
                if (!value) return;
                const frequencyPerYear = Number(value);
                patchRow(row.key, {
                  frequencyPerYear,
                  // The reconciliation category of a fund call follows its cadence.
                  category: row.isFundCall
                    ? fundCallCategoryOfFrequency(frequencyPerYear)
                    : row.category,
                });
              }}
              label={t('budgetV2.income.frequencyLabel', 'Fréquence')}
            />
            <div className={styles.perHousehold}>
              <Checkbox
                size="md"
                checked={row.perHousehold}
                onChange={event => patchRow(row.key, { perHousehold: event.currentTarget.checked })}
                label={t('budgetV2.income.perHousehold', 'Par foyer')}
              />
            </div>
          </BudgetSuggestionRow>
        ))}
      </div>

      <div className={styles.addRow}>
        <Select
          className={styles.addSelect}
          size="md"
          searchable
          data={options}
          value={null}
          onChange={addIncome}
          placeholder={t('budgetV2.income.addPlaceholder', 'Choisir une recette')}
          label={t('budgetV2.income.add', 'Ajouter une recette')}
        />
      </div>

      {householdCount === 0 || suggestedAmount == null ? (
        <p className={styles.noHousehold}>
          {t(
            'budgetV2.income.noHousehold',
            "Aucun foyer n'est défini sur ce bien : la suggestion d'appel de fonds et la quote-part par foyer ne peuvent pas être calculées."
          )}
        </p>
      ) : (
        <div className={styles.suggestion}>
          <span className={styles.suggestionText}>
            {t('budgetV2.income.suggestionIntro', 'Pour équilibrer ce budget :')}
            <b className={styles.suggestionAmount}>
              {t('budgetV2.income.suggestionAmount', '{{amount}} {{cadence}} et par foyer', {
                amount: formatEuros(suggestedAmount),
                cadence: t(
                  `budgetV2.income.cadence.${FREQUENCY_KEYS[String(suggestionFrequency)] ?? 'yearly'}`,
                  'par an'
                ),
              })}
            </b>
          </span>
          {fundCallRow ? (
            <Button size="md" onClick={applySuggestion}>
              {t('budgetV2.income.applySuggestion', 'Utiliser ce montant')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default IncomeStep;
